import { redirect } from 'react-router-dom';
import { useContract } from '@/core/api';
import type { useAuth } from '@/core/auth';
import { AuthLayout, GmnLayout } from '@/core/layouts';
import { CustomerLayoutWrapper } from '@/core/layouts/customer-layout-wrapper';
import { ManageCustomer as CustomerSelfService } from '@/features/customer/customer';
import {
  CustomersOverview,
  HelicopterServiceProvidersOverview,
  ManageCustomer as ManageCustomerAdmin,
  ManageHelicopterServiceProvider,
} from '@/features/gmn/customers';
import { EmailDistribution } from '@/features/gmn/email-distribution';
import { ManageContractMetadata } from '@/features/gmn/metadata';
import { ManageObjectCategory, ObjectCategoriesOverview } from '@/features/gmn/object-categories';
import { UserGroupsOverview } from '@/features/gmn/user-groups';
import { MyAccount } from '@/features/shared/account';
import {
  AcceptInvite,
  Login,
  MicrosoftCallback,
  RequestPasswordReset,
  ResetPassword,
} from '@/features/shared/auth';
import {
  ContractCreateWizard,
  ContractDetails,
  ContractsOverview,
} from '@/features/shared/contracts';
import { Notifications } from '@/features/shared/notifications';
import { ManageObject, ObjectsOverview, SystemShipDetails } from '@/features/shared/objects';
import {
  OperationCreate,
  OperationDetails,
  OperationsOverview,
} from '@/features/shared/operations';
import { UserManagement } from '@/features/shared/users';
import type { CustomRouteObjectUnion } from '@/shared/types';
import {
  canReadService,
  DEFAULT_GMN_GROUP_KEYS,
  JUH_DISPATCHER_GROUP_KEY,
  resolveUserGroup,
  resolveUserGroupDomain,
} from '@/shared/utils/permissions';
import { privateRoute, publicRoute, unknownRoute } from './guards';
import { getDefaultRouteForUser } from './navigation-routes';

const redirectToLogin = (request: Request) => {
  const url = new URL(request.url);
  const redirectPath = `${url.pathname}${url.search}${url.hash}`;
  return redirect(`/auth/login?redirect=${encodeURIComponent(redirectPath)}`);
};

const normalizeObjectTab = (tab?: string) => (tab === 'activity' ? 'activity' : 'details');

const redirectToDetailsWithCurrentSearch = (request: Request) => {
  const url = new URL(request.url);
  return redirect(`details${url.search}${url.hash}`);
};

const resolveObjectsRedirect = (
  user: ReturnType<typeof useAuth>['user'],
  request: Request,
  id?: string,
  tab?: string
) => {
  if (!user) {
    return redirectToLogin(request);
  }
  const url = new URL(request.url);
  const group = resolveUserGroup(user);
  if (!group || !canReadService(group, 'objects')) {
    return redirect(getDefaultRouteForUser(user));
  }

  const domain = resolveUserGroupDomain(user);
  const basePath = domain === 'customer' ? '/c/objects' : '/gmn/objects';
  if (!id) {
    return redirect(`${basePath}${url.search}${url.hash}`);
  }
  return redirect(`${basePath}/${id}/${normalizeObjectTab(tab)}${url.search}${url.hash}`);
};

export const createRoutes = (user: ReturnType<typeof useAuth>['user']) =>
  [
    {
      path: '*',
      loader: ({ request }) => unknownRoute(user, request),
      element: <div>Unknown</div>,
    },
    {
      path: '/auth',
      element: <AuthLayout />,
      loader: ({ request }) => publicRoute(user, request),
      children: [
        {
          path: 'login',
          element: <Login />,
        },
        {
          path: 'microsoft/callback',
          element: <MicrosoftCallback />,
        },
        {
          path: 'forgot-password',
          element: <RequestPasswordReset />,
        },
        {
          path: 'reset-password',
          element: <ResetPassword />,
        },
        {
          path: 'accept-invite',
          element: <AcceptInvite />,
        },
      ],
    },
    {
      path: '/objects',
      element: <div>Redirecting...</div>,
      children: [
        {
          index: true,
          loader: ({ request }) => resolveObjectsRedirect(user, request),
        },
        {
          path: ':id',
          loader: ({ request, params }) => resolveObjectsRedirect(user, request, params.id),
        },
        {
          path: ':id/:tab',
          loader: ({ request, params }) =>
            resolveObjectsRedirect(user, request, params.id, params.tab),
        },
      ],
    },
    {
      path: '/c',
      loader: ({ request }) => privateRoute({ domain: 'customer' })(user, request),
      element: <CustomerLayoutWrapper />,
      children: [
        {
          index: true,
          loader: () => redirect(getDefaultRouteForUser(user)),
        },

        {
          path: 'objects',
          loader: ({ request }) =>
            privateRoute({ domain: 'customer', service: 'objects' })(user, request),
          handle: {
            breadcrumb: () => 'objects.myObjects',
          },
          children: [
            {
              index: true,
              element: <ObjectsOverview filterForCurrentUserCustomer={true} />,
            },
            {
              path: 'new',
              element: <ManageObject />,
              handle: {
                breadcrumb: () => 'objects.newObject',
              },
            },
            {
              path: 'ships/:id/details',
              element: <SystemShipDetails />,
              loader: ({ request }) =>
                privateRoute({ domain: 'customer', service: 'objects' })(user, request),
              handle: {
                breadcrumb: () => 'objects.objectDetails',
              },
            },
            {
              path: 'ships/:id/history',
              element: <SystemShipDetails />,
              loader: ({ request }) =>
                privateRoute({ domain: 'customer', service: 'objects' })(user, request),
              handle: {
                breadcrumb: () => 'objects.objectDetails',
              },
            },
            {
              path: ':id',
              element: <ManageObject />,
              handle: {
                breadcrumb: () => 'objects.editObject',
              },
              children: [
                {
                  index: true,
                  loader: ({ request }) => redirectToDetailsWithCurrentSearch(request),
                },
                {
                  path: 'details',
                  element: <ManageObject />,
                  handle: {
                    tab: 'details',
                  },
                },
                {
                  path: 'files',
                  element: <ManageObject />,
                  handle: {
                    tab: 'files',
                  },
                },
                {
                  path: 'activity',
                  element: <ManageObject />,
                  handle: {
                    tab: 'activity',
                  },
                },
                {
                  path: 'permissions',
                  element: <ManageObject />,
                  handle: {
                    tab: 'permissions',
                  },
                },
              ],
            },
          ],
        },

        {
          path: 'my-operations',
          loader: ({ request }) =>
            privateRoute({ domain: 'customer', service: 'operations' })(user, request),
          handle: {
            breadcrumb: () => 'operations.myOperations',
          },
          children: [
            {
              index: true,
              element: <OperationsOverview mode="customer" />,
            },
            {
              path: ':id',
              element: <OperationDetails />,
              handle: {
                breadcrumb: () => 'operations.operationDetails',
              },
              children: [
                {
                  index: true,
                  loader: () => redirect('details'),
                },
                {
                  path: 'details',
                  element: <OperationDetails />,
                  handle: {
                    tab: 'details',
                  },
                },
                {
                  path: 'permissions',
                  element: <OperationDetails />,
                  handle: {
                    tab: 'permissions',
                  },
                },
              ],
            },
          ],
        },

        {
          path: 'my-customer/customer',
          loader: ({ request }) =>
            privateRoute({ domain: 'customer', service: 'customers' })(user, request),
          element: <CustomerSelfService />,
          handle: {
            breadcrumb: () => 'customers.customer',
          },
          children: [
            {
              index: true,
              loader: () => redirect('general'),
            },
            {
              path: 'general',
              handle: {
                tab: 'general',
              },
            },
            {
              path: 'notifications',
              handle: {
                tab: 'notifications',
              },
            },
            {
              path: 'users',
              handle: {
                tab: 'users',
              },
            },
          ],
        },
        {
          path: 'my-customer',
          loader: () => redirect('/c/my-customer/customer'),
        },
        {
          path: 'my-customer/users',
          loader: () => redirect('/c/my-customer/customer'),
        },
        {
          path: 'my-account',
          element: <MyAccount />,
          handle: {
            breadcrumb: () => 'auth.myAccount',
          },
        },
        {
          path: 'notifications',
          loader: ({ request }) =>
            privateRoute({ domain: 'customer', service: 'notifications' })(user, request),
          handle: {
            breadcrumb: () => 'common.notifications',
          },
          children: [
            {
              index: true,
              loader: () => redirect('unread'),
            },
            {
              path: 'unread',
              element: <Notifications />,
            },
            {
              path: 'read',
              element: <Notifications />,
            },
            {
              path: 'archived',
              element: <Notifications />,
            },
          ],
        },
        {
          path: 'contract-management',
          loader: ({ request }) =>
            privateRoute({ domain: 'customer', service: 'contracts' })(user, request),
          children: [
            {
              path: 'contracts',
              handle: {
                breadcrumb: () => 'contracts.contracts',
              },
              children: [
                {
                  index: true,
                  element: <ContractsOverview />,
                },
                {
                  path: ':id',
                  element: <ContractDetails />,
                  handle: {
                    breadcrumb: (match) => ({
                      query: {
                        queryFn: useContract,
                        id: match.params.id!,
                        dataKey: 'title',
                      },
                    }),
                  },
                  children: [
                    {
                      index: true,
                      loader: () => redirect('general'),
                    },
                    {
                      path: 'general',
                      handle: {
                        tab: 'general',
                      },
                    },
                    {
                      path: 'billing',
                      handle: {
                        tab: 'billing',
                      },
                    },
                    {
                      path: 'files',
                      handle: {
                        tab: 'files',
                      },
                    },
                    {
                      path: 'subcontracts',
                      handle: {
                        tab: 'subcontracts',
                      },
                    },
                    {
                      path: 'permissions',
                      handle: {
                        tab: 'permissions',
                      },
                    },
                    {
                      path: 'activity',
                      handle: {
                        tab: 'activity',
                      },
                    },
                  ],
                },
              ],
            },
          ],
        },
      ],
    },
    {
      path: '/gmn',
      loader: ({ request }) => privateRoute({ domain: 'gmn' })(user, request),
      element: <GmnLayout />,
      children: [
        {
          index: true,
          loader: () => redirect('/gmn/contract-management/contracts'),
        },

        {
          path: 'operations',
          loader: ({ request }) =>
            privateRoute({ domain: 'gmn', service: 'operations' })(user, request),
          handle: {
            breadcrumb: () => 'operations.operations',
          },
          children: [
            {
              index: true,
              element: <OperationsOverview mode="gmn" />,
            },
            {
              path: 'new',
              element: <OperationCreate />,
              loader: ({ request }) =>
                privateRoute({ domain: 'gmn', service: 'operations', action: 'create' })(
                  user,
                  request
                ),
              handle: {
                breadcrumb: () => 'operations.newOperation',
              },
            },
            {
              path: ':id',
              element: <OperationDetails />,
              loader: ({ request }) =>
                privateRoute({ domain: 'gmn', service: 'operations' })(user, request),
              handle: {
                breadcrumb: () => 'operations.operationDetails',
              },
              children: [
                {
                  index: true,
                  loader: () => redirect('details'),
                },
                {
                  path: 'details',
                  element: <OperationDetails />,
                  handle: {
                    tab: 'details',
                  },
                },
                {
                  path: 'activity',
                  element: <OperationDetails />,
                  handle: {
                    tab: 'activity',
                  },
                },
                {
                  path: 'permissions',
                  element: <OperationDetails />,
                  handle: {
                    tab: 'permissions',
                  },
                },
              ],
            },
          ],
        },

        {
          path: 'objects',
          loader: ({ request }) =>
            privateRoute({ domain: 'gmn', service: 'objects' })(user, request),
          handle: {
            breadcrumb: () => 'objects.objects',
          },
          children: [
            {
              index: true,
              element: <ObjectsOverview />,
            },
            {
              path: 'ships/:id/details',
              element: <SystemShipDetails />,
              loader: ({ request }) =>
                privateRoute({ domain: 'gmn', service: 'objects' })(user, request),
              handle: {
                breadcrumb: () => 'objects.objectDetails',
              },
            },
            {
              path: 'ships/:id/history',
              element: <SystemShipDetails />,
              loader: ({ request }) =>
                privateRoute({ domain: 'gmn', service: 'objects' })(user, request),
              handle: {
                breadcrumb: () => 'objects.objectDetails',
              },
            },
            {
              path: 'new',
              element: <ManageObject />,
              loader: ({ request }) =>
                privateRoute({
                  domain: 'gmn',
                  service: 'objects',
                  action: 'create',
                  // JUH dispatchers may create island/clinic/land/fuel-station objects; the
                  // category restriction is enforced in the form and backend.
                  bypassServiceCheckForGroupKeys: [JUH_DISPATCHER_GROUP_KEY],
                })(user, request),
              handle: {
                breadcrumb: () => 'objects.newObject',
              },
            },
            {
              path: ':id',
              element: <ManageObject />,
              loader: ({ request }) =>
                privateRoute({ domain: 'gmn', service: 'objects' })(user, request),
              handle: {
                breadcrumb: () => 'objects.editObject',
              },
              children: [
                {
                  index: true,
                  loader: ({ request }) => redirectToDetailsWithCurrentSearch(request),
                },
                {
                  path: 'details',
                  element: <ManageObject />,
                  handle: {
                    tab: 'details',
                  },
                },
                {
                  path: 'files',
                  element: <ManageObject />,
                  handle: {
                    tab: 'files',
                  },
                },
                {
                  path: 'activity',
                  element: <ManageObject />,
                  handle: {
                    tab: 'activity',
                  },
                },
                {
                  path: 'permissions',
                  element: <ManageObject />,
                  handle: {
                    tab: 'permissions',
                  },
                },
              ],
            },
          ],
        },

        {
          path: 'contract-management',
          loader: ({ request }) =>
            privateRoute({ domain: 'gmn', service: 'contracts' })(user, request),
          children: [
            {
              path: 'contracts',
              handle: {
                breadcrumb: () => 'contracts.contracts',
              },
              children: [
                {
                  index: true,
                  element: <ContractsOverview />,
                },
                {
                  path: ':id',
                  element: <ContractDetails />,
                  loader: ({ request }) =>
                    privateRoute({ domain: 'gmn', service: 'contracts' })(user, request),
                  handle: {
                    breadcrumb: (match) => ({
                      query: {
                        queryFn: useContract,
                        id: match.params.id!,
                        dataKey: 'title',
                      },
                    }),
                  },
                  children: [
                    {
                      index: true,
                      loader: () => redirect('general'),
                    },
                    {
                      path: 'general',
                      handle: {
                        tab: 'general',
                      },
                    },
                    {
                      path: 'helicopters',
                      handle: {
                        tab: 'helicopters',
                      },
                    },
                    {
                      path: 'billing',
                      handle: {
                        tab: 'billing',
                      },
                    },
                    {
                      path: 'files',
                      handle: {
                        tab: 'files',
                      },
                    },
                    {
                      path: 'subcontracts',
                      handle: {
                        tab: 'subcontracts',
                      },
                    },
                    {
                      path: 'permissions',
                      handle: {
                        tab: 'permissions',
                      },
                    },
                    {
                      path: 'activity',
                      handle: {
                        tab: 'activity',
                      },
                    },
                  ],
                },
                {
                  path: 'new',
                  element: <ContractCreateWizard />,
                  loader: ({ request }) =>
                    privateRoute({ domain: 'gmn', service: 'contracts', action: 'create' })(
                      user,
                      request
                    ),
                  handle: {
                    breadcrumb: () => 'contracts.newContract',
                  },
                },
              ],
            },
            {
              path: 'metadata',
              element: <ManageContractMetadata />,
              loader: ({ request }) =>
                privateRoute({
                  domain: 'gmn',
                  service: 'contracts',
                  allowedGroupKeys: ['gmn-admin'],
                })(user, request),
              handle: {
                breadcrumb: () => 'contracts.metadata',
              },
            },
          ],
        },

        {
          path: 'customer-management',
          children: [
            {
              path: 'customers',
              handle: {
                breadcrumb: () => 'customers.customers',
              },
              children: [
                {
                  index: true,
                  element: <CustomersOverview />,
                },
                {
                  path: 'new',
                  element: <ManageCustomerAdmin />,
                  loader: ({ request }) =>
                    privateRoute({ domain: 'gmn', service: 'customers', action: 'create' })(
                      user,
                      request
                    ),
                  handle: {
                    breadcrumb: () => 'customers.newCustomer',
                  },
                },
                {
                  path: ':id',
                  element: <ManageCustomerAdmin />,
                  loader: ({ request }) =>
                    privateRoute({ domain: 'gmn', service: 'customers' })(user, request),
                  handle: {
                    breadcrumb: () => 'customers.editCustomer',
                  },
                  children: [
                    {
                      index: true,
                      loader: () => redirect('general'),
                    },
                    {
                      path: 'general',
                      handle: {
                        tab: 'general',
                      },
                    },
                    {
                      path: 'notifications',
                      handle: {
                        tab: 'notifications',
                      },
                    },
                    {
                      path: 'users',
                      handle: {
                        tab: 'users',
                      },
                    },
                  ],
                },
              ],
            },
            {
              path: 'helicopter-service-providers',
              handle: {
                breadcrumb: () => 'customers.helicopterServiceProviders',
              },
              children: [
                {
                  index: true,
                  element: <HelicopterServiceProvidersOverview />,
                },
                {
                  path: 'new',
                  element: <ManageHelicopterServiceProvider />,
                  loader: ({ request }) =>
                    privateRoute({ domain: 'gmn', service: 'customers', action: 'create' })(
                      user,
                      request
                    ),
                  handle: {
                    breadcrumb: () => 'customers.newHelicopterServiceProvider',
                  },
                },
                {
                  path: ':id',
                  element: <ManageHelicopterServiceProvider />,
                  loader: ({ request }) =>
                    privateRoute({ domain: 'gmn', service: 'customers' })(user, request),
                  handle: {
                    breadcrumb: () => 'customers.editHelicopterServiceProvider',
                  },
                  children: [
                    {
                      index: true,
                      loader: () => redirect('general'),
                    },
                    {
                      path: 'general',
                      handle: {
                        tab: 'general',
                      },
                    },
                    {
                      path: 'helicopters',
                      handle: {
                        tab: 'helicopters',
                      },
                    },
                    {
                      path: 'notifications',
                      handle: {
                        tab: 'notifications',
                      },
                    },
                    {
                      path: 'users',
                      handle: {
                        tab: 'users',
                      },
                    },
                  ],
                },
              ],
            },
          ],
        },

        {
          path: 'settings',
          children: [
            {
              path: 'user-groups',
              element: <UserGroupsOverview />,
              loader: ({ request }) =>
                privateRoute({
                  domain: 'gmn',
                  service: 'users',
                  allowedGroupKeys: ['gmn-admin'],
                })(user, request),
              handle: {
                breadcrumb: () => 'userGroups.title',
              },
            },
            {
              path: 'users',
              element: <UserManagement allowedGroupKeys={[...DEFAULT_GMN_GROUP_KEYS]} />,
              loader: ({ request }) =>
                privateRoute({
                  domain: 'gmn',
                  service: 'users',
                  allowedGroupKeys: ['gmn-admin'],
                })(user, request),
              handle: {
                breadcrumb: () => 'users.users',
              },
            },
            {
              path: 'email-distribution',
              element: <EmailDistribution />,
              loader: ({ request }) =>
                privateRoute({
                  domain: 'gmn',
                  service: 'users',
                  allowedGroupKeys: ['gmn-admin'],
                })(user, request),
              handle: {
                breadcrumb: () => 'emailDistribution.title',
              },
            },
            {
              path: 'object-categories',
              loader: ({ request }) =>
                privateRoute({
                  domain: 'gmn',
                  service: 'object-categories',
                  allowedGroupKeys: ['gmn-admin'],
                })(user, request),
              handle: {
                breadcrumb: () => 'objectCategories.objectCategories',
              },
              children: [
                {
                  index: true,
                  element: <ObjectCategoriesOverview />,
                },
                {
                  path: 'new',
                  element: <ManageObjectCategory />,
                  handle: {
                    breadcrumb: () => 'objectCategories.newCategory',
                  },
                },
                {
                  path: ':id',
                  element: <ManageObjectCategory />,
                  handle: {
                    breadcrumb: () => 'objectCategories.editCategory',
                  },
                },
              ],
            },
            {
              path: 'metadata',
              element: <ManageContractMetadata />,
              loader: ({ request }) =>
                privateRoute({
                  domain: 'gmn',
                  service: 'contracts',
                  allowedGroupKeys: ['gmn-admin'],
                })(user, request),
              handle: {
                breadcrumb: () => 'metadata.contractMetadata',
              },
            },
          ],
        },

        {
          path: 'my-account',
          element: <MyAccount />,
          handle: {
            breadcrumb: () => 'auth.myAccount',
          },
        },
        {
          path: 'notifications',
          loader: ({ request }) =>
            privateRoute({ domain: 'gmn', service: 'notifications' })(user, request),
          handle: {
            breadcrumb: () => 'common.notifications',
          },
          children: [
            {
              index: true,
              loader: () => redirect('unread'),
            },
            {
              path: 'unread',
              element: <Notifications />,
            },
            {
              path: 'read',
              element: <Notifications />,
            },
            {
              path: 'archived',
              element: <Notifications />,
            },
          ],
        },
      ],
    },
  ] as CustomRouteObjectUnion[];
